/**
 * Token Refresh Hook
 * Proactively refreshes access token before expiration
 * Handles page visibility to avoid unnecessary refreshes
 *
 * Refresh token is stored in httpOnly cookie (sent automatically by browser)
 */

import { useEffect, useRef, useCallback } from 'react';
import { getToken, isTokenExpired, clearAuthData } from '../utils/auth';
import { refreshAccessToken } from '../lib/axios';

const CHECK_INTERVAL = 60000; // Check every 60 seconds
const REFRESH_BEFORE_EXPIRY = 300; // Refresh 5 minutes before expiry

export const useTokenRefresh = (onLogout?: () => void) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    // Prevent concurrent refresh attempts
    if (isRefreshingRef.current) {
      return false;
    }

    const currentToken = getToken();

    // No access token in memory - can't check if it needs refresh
    // The session restoration in AuthContext will handle this case
    if (!currentToken) {
      return false;
    }

    // Check if token needs refresh (within 5 minutes of expiry)
    if (!isTokenExpired(currentToken, REFRESH_BEFORE_EXPIRY)) {
      return false;
    }

    isRefreshingRef.current = true;

    try {
      // Refresh token is sent automatically via httpOnly cookie
      await refreshAccessToken();
      return true;
    } catch (error) {
      console.error('[useTokenRefresh] Failed to refresh token:', error);

      // Clear auth data and logout
      clearAuthData();
      onLogout?.();

      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [onLogout]);

  const checkAndRefreshToken = useCallback(async () => {
    // Don't refresh if page is hidden to save resources
    if (!document.hidden) {
      await refreshToken();
    }
  }, [refreshToken]);

  useEffect(() => {
    // Initial check
    checkAndRefreshToken();

    // Set up periodic checks
    intervalRef.current = setInterval(checkAndRefreshToken, CHECK_INTERVAL);

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkAndRefreshToken();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkAndRefreshToken]);

  return { refreshToken };
};
